/**
 * 辽宁 ETC 独立收银台 —— 与模板 lnetcPay.html 对齐（以模板为准）。
 * 立即支付：优先 SafePay（payDeepLink）；PC 二维码用 qrScanUrl（短桥，桥页内 tradePay）。
 *
 * 注意：HttpRequest 拦截器成功时已解包为 data，失败时 reject 整个 ApiResult。
 */
import { executeLnetcPay, fetchLnetcCashierPage } from '@/api/api'
import { buildQrImageSrc, computeQrPixelSize } from '@/utils/qrRender'

/** 与模板 applyPayload：有 payDeepLink 或 qrScanUrl 即视为出链成功 */
export function isExecutePayloadReady (d) {
  if (!d || d.expired) return false
  const deep = d.payDeepLink ? String(d.payDeepLink).trim() : ''
  const scan = d.qrScanUrl ? String(d.qrScanUrl).trim() : ''
  return !!(deep || scan)
}

export function isAlipayAppDeepLink (url) {
  const u = String(url || '').trim().toLowerCase()
  return u.startsWith('alipays://') || u.startsWith('alipay://') || u.startsWith('alipayqr://')
}

/** 支付宝 App 内 WebView（与模板 isMobileAlipay） */
export function isMobileAlipay () {
  if (typeof navigator === 'undefined') return false
  const ua = (navigator.userAgent || '').toLowerCase()
  return ua.includes('alipay') || ua.includes('aliapp') || ua.includes('nebula')
}

export function isMobileHandheld () {
  if (typeof navigator === 'undefined') return false
  const ua = (navigator.userAgent || '').toLowerCase()
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua)
}

export function isDesktopBrowser () {
  return !isMobileHandheld() && !isMobileAlipay()
}

/** 与模板 resolveLnetcImmediatePayUrl：优先 SafePay，否则短桥 */
export function resolveLnetcImmediatePayUrl (payload) {
  if (!payload) return ''
  const safepay = payload.payDeepLink ? String(payload.payDeepLink).trim() : ''
  if (safepay) return safepay
  return payload.qrScanUrl ? String(payload.qrScanUrl).trim() : ''
}

/** 与模板 shouldShowQr：仅 PC 展示二维码 */
export function shouldShowLnetcQrOnCashier () {
  return isDesktopBrowser()
}

/** 与模板 openPayUrl */
export function openPayNavigationUrl (url) {
  const u = String(url || '').trim()
  if (!u) return
  if (isAlipayAppDeepLink(u)) {
    try {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = u
      document.body.appendChild(iframe)
    } catch (e) { /* ignore */ }
    setTimeout(() => { window.location.href = u }, 120)
    return
  }
  if (isDesktopBrowser() && (u.startsWith('http://') || u.startsWith('https://'))) {
    window.open(u, '_blank', 'noopener,noreferrer')
    return
  }
  window.location.href = u
}

/** 与模板 applyLnetcImmediatePay */
export function applyLnetcImmediatePay (payload) {
  const url = resolveLnetcImmediatePayUrl(payload)
  if (!url) return false
  openPayNavigationUrl(url)
  return true
}

/** page 接口：拦截器已解包，返回 LnetcCashierPageVO（与 UnifiedPay 一致） */
export async function loadPageContext (orderId, token) {
  const ctx = await fetchLnetcCashierPage(orderId, token)
  if (!ctx) {
    throw new Error('页面加载失败')
  }
  return ctx
}

/** execute 接口：成功解包为 VO；业务失败 reject ApiResult */
export async function requestExecute (orderId, token) {
  try {
    const d = await executeLnetcPay(orderId, token)
    if (!d) throw new Error('网络异常')
    return d
  } catch (err) {
    if (err && err.data) {
      const vo = err.data
      if (vo.expired) {
        const expiredErr = new Error(vo.message || '订单已过期')
        expiredErr.expiredPayload = vo
        throw expiredErr
      }
      throw new Error(vo.message || err.message || '出链失败')
    }
    throw new Error((err && err.message) || '出链失败')
  }
}

/** PC 短 HTTPS 扫码桥二维码 */
export async function renderQrToElement (el, text) {
  if (!el || !text) return false
  const size = computeQrPixelSize(text)
  const src = await buildQrImageSrc(text, { pixelSize: size })
  if (!src) return false
  el.innerHTML = ''
  const img = document.createElement('img')
  img.src = src
  img.alt = '支付二维码'
  img.className = 'qr-rendered'
  img.style.width = `${size}px`
  img.style.maxWidth = '100%'
  img.style.height = 'auto'
  el.appendChild(img)
  return true
}

export function formatCountdown (leftMs) {
  const s = Math.max(0, Math.floor(leftMs / 1000))
  const h = String(Math.floor(s / 3600)).padStart(2, '0')
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const sec = String(s % 60).padStart(2, '0')
  return `${h}:${m}:${sec}`
}
